/**
 * useCollectionCounts - React hook for fetching collection counts
 *
 * Provides dynamic collection data from the Voyager API with
 * loading states and error handling.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchAllCollectionCounts,
  DEFAULT_COLLECTIONS_CONFIG,
  DEFAULT_COLLECTION_DEFINITIONS,
} from './CollectionsService';
import type {
  CollectionDefinition,
  CollectionCountResult,
  CollectionsApiConfig,
} from './CollectionsService';

export interface UseCollectionCountsResult {
  collections: CollectionCountResult[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useCollectionCounts = (
  definitions: CollectionDefinition[] = DEFAULT_COLLECTION_DEFINITIONS,
  config: CollectionsApiConfig = DEFAULT_COLLECTIONS_CONFIG,
  enabled: boolean = true,
): UseCollectionCountsResult => {
  const [collections, setCollections] = useState<CollectionCountResult[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const results = await fetchAllCollectionCounts(definitions, config);
      setCollections(results);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch collections';
      setError(errorMessage);
      // Set empty collections on error - the component can fall back to defaults
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, [definitions, config, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    collections,
    loading,
    error,
    refetch,
  };
};

export default useCollectionCounts;
